import { useState, useEffect, useCallback } from "react";
import {
	LOAN_PURPOSE_PERSONAL,
	LOAN_PURPOSE_BUSINESS,
	EMP_STAT_SELF_EMPLOYED,
	LOAN_PURPOSE_NOVATED_LEASE,
	LOAN_TYPE_BOAT,
	LOAN_PURPOSE_DEBT_CONSOLIDATION_REFINANCING,
	LOAN_TYPE_CAR,
	LOAN_TYPE_PERSONAL,
	LOAN_TYPE_TRUCK,
	LOAN_TYPE_EQUIPMENT,
	LOAN_TYPE_CARAVAN,
	LOAN_TYPE_MOTORCYCLE,
	LOAN_TYPE_BUSINESS,
	LOAN_PURPOSE_NEW,
	LOAN_TYPE_BIKE,
} from "@/lib/constants";
import {
	CurrencyDollarIcon,
	ArrowsClockwiseIcon,
	CarIcon,
	IslandIcon,
	SuitcaseIcon,
} from "@phosphor-icons/react";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui/radioGroupField";

export const DefaultLoanDetails = () => {
	const { formValues, setFormValue, clearFormError, setFormError } = useFormDataStore();
	const { selectedIndex, enabledFields, setFormErrors, setValidateFormDetails, formContainer } =
		useFormStateStore();

	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = selectedIndex === 0 ? 350 : 460;

		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	const { loanType, loanPurpose, assetType, purchaseStage, assetAge, purchaseFrom } = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		loanPurpose: false,
		assetType: false,
		purchaseStage: false,
	});

	// handle loan purpose change
	const handleLoanPurposeChange = useCallback(
		(value: string) => {
			setFormValue("loanPurpose", value);

			if (value === LOAN_PURPOSE_PERSONAL && formValues.assetType === "Commercial Boat") {
				setFormValue("assetType", "");
			}

			setFormValue(
				"employmentStatus",
				[
					LOAN_PURPOSE_PERSONAL,
					LOAN_PURPOSE_NOVATED_LEASE,
					LOAN_PURPOSE_DEBT_CONSOLIDATION_REFINANCING, // Personal Loan
					LOAN_PURPOSE_NEW,
					LOAN_PURPOSE_BUSINESS,
				].includes(value)
					? ""
					: EMP_STAT_SELF_EMPLOYED,
			);

			if (value === LOAN_PURPOSE_PERSONAL) {
				setFormValue("abnStatus", "");
				setFormValue("gstStatus", "");
			}

			setErrors((prevErrors) => ({
				...prevErrors,
				loanPurpose: !value, // Clear error when a selection is made
			}));
		},
		[setFormValue, setFormError, setErrors, clearFormError],
	);

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [loanPurpose, errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			loanPurpose: enabledFields.loanPurpose?.length == 0 ? false : !loanPurpose,
			assetType: enabledFields.assetType?.length == 0 ? false : !assetType,
			purchaseStage: enabledFields.purchaseStage?.length == 0 ? false : !purchaseStage,
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// 86cyywnev - Set Loan Purpose automatically if it has only one option
	useEffect(() => {
		if (enabledFields.loanPurpose?.length === 1) {
			setFormValue("loanPurpose", enabledFields.loanPurpose[0]?.value);
			enabledFields.loanPurpose[0]?.value === LOAN_PURPOSE_BUSINESS
				? setFormValue("employmentStatus", EMP_STAT_SELF_EMPLOYED)
				: enabledFields.loanPurpose[0]?.value === LOAN_PURPOSE_PERSONAL
					? null //Don't initialise employmentStatus if Personal loan
					: setFormValue("employmentStatus", "");
		}
	}, []);

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [loanPurpose, assetType, purchaseStage, assetAge, purchaseFrom]);

	// Reset formValues where it's not enabled.
	useEffect(() => {
		const fieldsToReset: (keyof typeof formValues)[] = [
			"loanPurpose",
			"assetType",
			"purchaseStage",
			"assetAge",
			"purchaseFrom",
		];

		fieldsToReset.forEach((field) => {
			if (enabledFields[field]?.length === 0) {
				setFormValue(field, ""); // Reset value when no options are available
				setErrors((prevErrors) => ({
					...prevErrors,
					field: false,
				}));
			}
		});
	}, [enabledFields, setFormValue, setErrors]);

	const loanPurposeLabel =
		{
			[LOAN_TYPE_CAR]: "Car Loan Purpose",
			[LOAN_TYPE_BOAT]: "Boat Loan Purpose",
			[LOAN_TYPE_TRUCK]: "Truck Loan Purpose",
			[LOAN_TYPE_EQUIPMENT]: "Equipment Loan Purpose",
			[LOAN_TYPE_CARAVAN]: "Caravan Loan Purpose",
			[LOAN_TYPE_MOTORCYCLE]: "Motorcycle Loan Purpose",
			[LOAN_TYPE_BIKE]: "Bike Loan Purpose",
			[LOAN_TYPE_PERSONAL]: "Personal Loan Purpose",
			[LOAN_TYPE_BUSINESS]: "Business Loan Purpose",
		}[loanType] || "Loan Purpose";

	return (
		<div className="jF-space-y-4 jF-m-2 jF-pb-4">
			{/* Loan Purpose */}
			{enabledFields.loanPurpose?.length > 1 && ( // Empty array means field is not supported.
				<RadioGroupField
					id="loanPurpose"
					label={loanPurposeLabel}
					value={loanPurpose || ""}
					options={enabledFields.loanPurpose.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon:
							item.value === LOAN_PURPOSE_PERSONAL ? (
								<IslandIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_BUSINESS ? (
								<SuitcaseIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_NOVATED_LEASE ? (
								<CarIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_NEW ? (
								<CurrencyDollarIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_DEBT_CONSOLIDATION_REFINANCING ? (
								<ArrowsClockwiseIcon size={24} weight="duotone" />
							) : null,
					}))}
					onChange={(value) => {
						handleLoanPurposeChange(value);
					}}
					required={true}
					error={errors.loanPurpose ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={loanType === LOAN_TYPE_CAR ? 3 : 2}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}

			{/* Asset Type - For Boat and Equipment Loans only*/}
			{enabledFields.assetType?.length != 0 && ( // Empty array means field is not supported.
				<RadioGroupField
					id="assetType"
					label="Asset Type"
					value={assetType || ""}
					options={enabledFields.assetType
						.filter((item) =>
							loanType === LOAN_TYPE_BOAT && loanPurpose === LOAN_PURPOSE_PERSONAL
								? item.value !== "Commercial Boat"
								: true,
						)
						.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
					onChange={(value) => {
						setFormValue("assetType", value);
						setErrors((prevErrors) => ({
							...prevErrors,
							assetType: !value, // Clear error when a selection is made
						}));
					}}
					required={true}
					error={errors.assetType ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={
						formValues.loanType === LOAN_TYPE_EQUIPMENT
							? 3
							: formValues.loanType === LOAN_TYPE_BOAT &&
								  formValues.loanPurpose !== LOAN_PURPOSE_PERSONAL
								? 5
								: 4
					}
					showIcons={false}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}

			{/* Purchase Stage */}
			{enabledFields.purchaseStage?.length != 0 && (
				<RadioGroupField
					id="purchaseStage"
					label="Do you have one in mind?"
					value={purchaseStage || ""}
					options={enabledFields.purchaseStage.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon: null,
					}))}
					onChange={(value) => {
						setFormValue("purchaseStage", value);
						setErrors((prevErrors) => ({
							...prevErrors,
							purchaseStage: !value, // Clear error when a selection is made
						}));
					}}
					required={true}
					error={errors.purchaseStage ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={3}
					showIcons={false}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}
		</div>
	);
};
